import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { getReport, getSettings, getCustomerDebtReport } from '../api/client';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import type { CustomerDebtDetail } from '../components/CustomerDetailsModal';
import PrintableReport, { PRINT_STYLES } from '../components/PrintableReport';
import { createRoot } from 'react-dom/client';

// ── Types ────────────────────────────────────────────────────────────

interface DailyTotal {
  date: string;
  invoices_count: number;
  invoices_amount: number;
  receipts_count: number;
  receipts_amount: number;
}

interface CustomerBreakdown {
  customer_id: number;
  customer_name: string;
  invoices_count: number;
  invoices_amount: number;
  receipts_count: number;
  receipts_amount: number;
  unpaid_amount: number;
  aged_debt_amount: number;
  total_amount: number;
}

interface DocumentTypeBreakdown {
  document_type: number;
  document_name: string;
  count: number;
  total_amount: number;
}

interface ReportData {
  period: string;
  from_date: string;
  to_date: string;
  total_invoices: number;
  total_invoices_amount: number;
  total_receipts: number;
  total_receipts_amount: number;
  total_documents: number;
  total_unpaid_amount: number;
  total_amount: number;
  vat_rate: number;
  prev_total_invoices: number;
  prev_total_invoices_amount: number;
  prev_total_receipts: number;
  prev_total_receipts_amount: number;
  daily_totals: DailyTotal[];
  customer_breakdown: CustomerBreakdown[];
  invoice_type_breakdown: DocumentTypeBreakdown[];
  receipt_type_breakdown: DocumentTypeBreakdown[];
}

type Period = 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'last_6_months' | 'this_year' | 'custom';

const CHART_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

// ── Helpers ──────────────────────────────────────────────────────────

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
}

function formatNIS(amount: number): string {
  return `₪${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function shortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function formatFilterDate(iso: string, locale?: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(locale || undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function fuzzyMatch(query: string, target: string): number {
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  if (t.includes(q)) return 0;

  let qi = 0;
  let score = 0;
  let lastMatchIdx = -1;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      if (lastMatchIdx >= 0) {
        score += ti - lastMatchIdx - 1;
      }
      lastMatchIdx = ti;
      qi++;
    }
  }

  return qi === q.length ? score + 1 : -1;
}

function fuzzySearchCustomers(customers: CustomerBreakdown[], query: string): CustomerBreakdown[] {
  if (!query.trim()) return customers;

  const q = query.trim();

  return customers
    .map((customer) => ({
      customer,
      score: fuzzyMatch(q, customer.customer_name || String(customer.customer_id)),
    }))
    .filter((item) => item.score >= 0)
    .sort((a, b) => a.score - b.score)
    .map((item) => item.customer);
}

// ── Sub-components ───────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  prevValue,
  format = 'number',
  t,
}: {
  label: string;
  value: number;
  prevValue?: number;
  format?: 'number' | 'currency';
  t: (key: string) => string;
}) {
  const display = format === 'currency' ? formatNIS(value) : value.toLocaleString();
  const change = prevValue !== undefined ? pctChange(value, prevValue) : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-1">
      <span className="text-sm text-gray-500 font-medium">{label}</span>
      <span className="text-2xl font-bold text-gray-900">{display}</span>
      {change !== null && (
        <span className={`text-xs font-medium flex items-center gap-1 ${change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
          {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(1)}%
          <span className="text-gray-400 font-normal ltr:ml-1 rtl:mr-1">{t('reports.vsLastPeriod')}</span>
        </span>
      )}
    </div>
  );
}

function PeriodSelector({
  period,
  setPeriod,
  customFrom,
  setCustomFrom,
  customTo,
  setCustomTo,
  onApply,
  t,
}: {
  period: Period;
  setPeriod: (p: Period) => void;
  customFrom: string;
  setCustomFrom: (v: string) => void;
  customTo: string;
  setCustomTo: (v: string) => void;
  onApply: () => void;
  t: (key: string) => string;
}) {
  const periods: { key: Period; label: string }[] = [
    { key: 'today', label: t('reports.periodToday') },
    { key: 'this_week', label: t('reports.periodThisWeek') },
    { key: 'this_month', label: t('reports.periodThisMonth') },
    { key: 'this_quarter', label: t('reports.periodThisQuarter') },
    { key: 'last_6_months', label: t('reports.periodLast6Months') },
    { key: 'this_year', label: t('reports.periodThisYear') },
    { key: 'custom', label: t('reports.periodCustom') },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {periods.map((p) => (
        <button
          key={p.key}
          onClick={() => setPeriod(p.key)}
          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
            period === p.key
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300 hover:text-primary-600'
          }`}
        >
          {p.label}
        </button>
      ))}
      {period === 'custom' && (
        <div className="flex items-center gap-2 ltr:ml-2 rtl:mr-2">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
          />
          <span className="text-gray-400">–</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
          />
          <button
            onClick={onApply}
            className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            {t('reports.apply')}
          </button>
        </div>
      )}
    </div>
  );
}

function CustomerTable({
  data,
  onSelect,
  debtAgeThresholdDays,
  t,
}: {
  data: CustomerBreakdown[];
  onSelect: (customer: CustomerBreakdown) => void;
  debtAgeThresholdDays: number;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const [sortKey, setSortKey] = useState<'unpaid_amount' | 'aged_debt_amount' | 'total_amount' | 'invoices_amount' | 'receipts_amount' | 'customer_name'>('unpaid_amount');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => fuzzySearchCustomers(data, query), [data, query]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortArrow = ({ col }: { col: typeof sortKey }) =>
    sortKey === col ? <span className="ltr:ml-1 rtl:mr-1">{sortDir === 'asc' ? '↑' : '↓'}</span> : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h3 className="text-base font-semibold text-gray-800">{t('reports.customerBreakdown')}</h3>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('reports.customerSearchPlaceholder')}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none transition-colors placeholder:text-gray-400 focus:border-primary-400 md:w-80"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-500">
              <th
                className="px-5 py-3 text-start font-medium cursor-pointer hover:text-gray-700"
                onClick={() => toggleSort('customer_name')}
              >
                {t('reports.customerName')}
                <SortArrow col="customer_name" />
              </th>
              <th
                className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700"
                onClick={() => toggleSort('invoices_amount')}
              >
                {t('reports.invoicesAmount')}
                <SortArrow col="invoices_amount" />
              </th>
              <th
                className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700"
                onClick={() => toggleSort('receipts_amount')}
              >
                {t('reports.receiptsAmount')}
                <SortArrow col="receipts_amount" />
              </th>
              <th
                className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700"
                onClick={() => toggleSort('unpaid_amount')}
              >
                {t('reports.unpaidAmount')}
                <SortArrow col="unpaid_amount" />
              </th>
              <th
                className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700"
                onClick={() => toggleSort('aged_debt_amount')}
              >
                {t('reports.agedDebtAmount', { days: debtAgeThresholdDays })}
                <SortArrow col="aged_debt_amount" />
              </th>
              <th
                className="px-5 py-3 text-end font-medium cursor-pointer hover:text-gray-700"
                onClick={() => toggleSort('total_amount')}
              >
                {t('reports.totalAmount')}
                <SortArrow col="total_amount" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr
                key={c.customer_id}
                className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer"
                onClick={() => onSelect(c)}
              >
                <td className="px-5 py-3 font-medium text-gray-800">
                  <div>{c.customer_name || `#${c.customer_id}`}</div>
                  <div className="text-xs font-normal text-primary-600 mt-1">{t('reports.openDetails')}</div>
                </td>
                <td className="px-5 py-3 text-end text-gray-600">{formatNIS(c.invoices_amount)}</td>
                <td className="px-5 py-3 text-end text-gray-600">{formatNIS(c.receipts_amount)}</td>
                <td className="px-5 py-3 text-end font-semibold text-amber-700">{formatNIS(c.unpaid_amount)}</td>
                <td className="px-5 py-3 text-end font-semibold text-red-700">{formatNIS(c.aged_debt_amount)}</td>
                <td className="px-5 py-3 text-end font-semibold text-gray-800">{formatNIS(c.total_amount)}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-gray-400">
                  {t('reports.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
// ── Main Component ───────────────────────────────────────────────────

export default function Reports() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReportData | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerBreakdown | null>(null);
  const [debtAgeThresholdDays, setDebtAgeThresholdDays] = useState(60);

  const [period, setPeriod] = useState<Period>('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const fetchReport = useCallback(async (p: Period, from?: string, to?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { period: p };
      if (p === 'custom' && from && to) {
        params.from_date = from;
        params.to_date = to;
      }
      const res = await getReport(params);
      setData(res.data);
    } catch {
      setError(t('reports.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Fetch on period change (non-custom)
  useEffect(() => {
    getSettings()
      .then((res) => {
        const value = Number(res.data?.debt_age_threshold_days ?? res.data?.settings?.debt_age_threshold_days ?? 60);
        setDebtAgeThresholdDays(Number.isFinite(value) && value > 0 ? value : 60);
      })
      .catch(() => {
        setDebtAgeThresholdDays(60);
      });
  }, []);

  useEffect(() => {
    if (period !== 'custom') {
      fetchReport(period);
    }
  }, [period, fetchReport]);

  useEffect(() => {
    if (period !== 'custom' || !customFrom || !customTo) {
      return;
    }
    if (customFrom > customTo) {
      return;
    }
    fetchReport('custom', customFrom, customTo);
  }, [period, customFrom, customTo, fetchReport]);

  const handleApplyCustom = () => {
    if (customFrom && customTo && customFrom <= customTo) {
      fetchReport('custom', customFrom, customTo);
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExportPrint = useCallback(async () => {
    if (!data || exporting) return;
    setExporting(true);
    try {
      const results = await Promise.allSettled(
        data.customer_breakdown.map((c) =>
          getCustomerDebtReport(c.customer_id).then((res) => ({
            id: c.customer_id,
            detail: res.data as CustomerDebtDetail,
          }))
        )
      );

      const detailsMap = new Map<number, CustomerDebtDetail>();
      for (const r of results) {
        if (r.status === 'fulfilled') {
          detailsMap.set(r.value.id, r.value.detail);
        }
      }

      const dir = i18n.dir();
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      printWindow.document.write(`<!DOCTYPE html>
<html lang="${i18n.language}" dir="${dir}">
<head>
  <meta charset="UTF-8" />
  <title>${t('reports.printTitle')}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body><div id="print-root"></div></body>
</html>`);
      printWindow.document.close();

      const container = printWindow.document.getElementById('print-root')!;
      const root = createRoot(container);
      root.render(
        <PrintableReport
          summary={{
            from_date: data.from_date,
            to_date: data.to_date,
            total_invoices_amount: data.total_invoices_amount,
            total_receipts_amount: data.total_receipts_amount,
            total_unpaid_amount: data.total_unpaid_amount,
            total_amount: data.total_amount,
            total_documents: data.total_documents,
          }}
          customers={data.customer_breakdown}
          customerDetails={detailsMap}
          debtAgeThresholdDays={debtAgeThresholdDays}
          t={t}
          locale={i18n.language}
          dir={dir as 'ltr' | 'rtl'}
        />
      );

      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);
    } finally {
      setExporting(false);
    }
  }, [data, exporting, debtAgeThresholdDays, i18n, t]);

  const handlePeriodChange = (p: Period) => {
    setPeriod(p);
  };

  // Chart data
  const trendData = useMemo(() => {
    if (!data) return [];
    return data.daily_totals.map((d) => ({
      ...d,
      label: shortDate(d.date),
      total: d.invoices_amount + d.receipts_amount,
    }));
  }, [data]);

  const topCustomers = useMemo(() => {
    if (!data) return [];
    return data.customer_breakdown.slice(0, 10);
  }, [data]);

  const pieData = useMemo(() => {
    if (!data) return [];
    return [
      ...data.invoice_type_breakdown.map((d) => ({ name: d.document_name || `Type ${d.document_type}`, value: d.total_amount })),
      ...data.receipt_type_breakdown.map((d) => ({ name: d.document_name || `Type ${d.document_type}`, value: d.total_amount })),
    ].filter((d) => d.value > 0);
  }, [data]);

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reports.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('reports.subtitle')}</p>
        </div>
        {data && !loading && (
          <button
            onClick={handleExportPrint}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <>
                <span className="animate-spin inline-block h-4 w-4 border-2 border-gray-300 border-t-primary-600 rounded-full" />
                {t('reports.exportLoading')}
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                {t('reports.exportPrint')}
              </>
            )}
          </button>
        )}
      </div>

      {/* Period Selector */}
      <PeriodSelector
        period={period}
        setPeriod={handlePeriodChange}
        customFrom={customFrom}
        setCustomFrom={setCustomFrom}
        customTo={customTo}
        setCustomTo={setCustomTo}
        onApply={handleApplyCustom}
        t={t}
      />

      {data && !loading && (
        <div className="text-sm text-gray-500">
          {t('reports.filteredRange', {
            from: formatFilterDate(data.from_date, i18n.language),
            to: formatFilterDate(data.to_date, i18n.language),
          })}
        </div>
      )}

      {/* Loading / Error */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          <span className="ltr:ml-3 rtl:mr-3 text-gray-500">{t('reports.loading')}</span>
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label={t('reports.totalRevenue')}
              value={data.total_amount}
              prevValue={data.prev_total_invoices_amount}
              format="currency"
              t={t}
            />
            <KpiCard
              label={t('reports.unpaidAmount')}
              value={data.total_unpaid_amount}
              prevValue={Math.max(data.prev_total_invoices_amount - data.prev_total_receipts_amount, 0)}
              format="currency"
              t={t}
            />
            <KpiCard
              label={t('reports.receiptsCount')}
              value={data.total_receipts}
              prevValue={data.prev_total_receipts}
              t={t}
            />
            <KpiCard
              label={t('reports.totalDocuments')}
              value={data.total_documents}
              prevValue={data.prev_total_invoices + data.prev_total_receipts}
              t={t}
            />
          </div>

          {/* Customer Breakdown Table */}
          <CustomerTable data={data.customer_breakdown} onSelect={(customer) => {
            setSelectedCustomer(customer);
          }} debtAgeThresholdDays={debtAgeThresholdDays} t={t} />

          {/* Revenue Trend Chart */}
          {trendData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="text-base font-semibold text-gray-800 mb-4">{t('reports.revenueTrend')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₪${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value) => [
                      formatNIS(Number(value)),
                      '',
                    ]}
                    labelFormatter={(label) => label}
                  />
                  <Legend
                    formatter={(value: string) =>
                      value === 'invoices_amount' ? t('reports.invoicesCount') : t('reports.receiptsCount')
                    }
                  />
                  <Bar dataKey="invoices_amount" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={20} />
                  <Line
                    dataKey="receipts_amount"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Charts Row: Top Customers + Pie */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Customers */}
            {topCustomers.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="text-base font-semibold text-gray-800 mb-4">{t('reports.topCustomers')}</h3>
                <ResponsiveContainer width="100%" height={Math.max(250, topCustomers.length * 40)}>
                  <BarChart data={topCustomers} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `₪${(v / 1000).toFixed(0)}k`} />
                    <YAxis
                      dataKey="customer_name"
                      type="category"
                      tick={{ fontSize: 11 }}
                      width={120}
                    />
                    <Tooltip formatter={(value) => [formatNIS(Number(value)), t('reports.totalAmount')]} />
                    <Bar dataKey="total_amount" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Document Type Pie */}
            {pieData.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="text-base font-semibold text-gray-800 mb-4">{t('reports.documentTypes')}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={110}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [formatNIS(Number(value))]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Date range info */}
          <div className="text-xs text-gray-400 text-center">
            {t('reports.vatRate')}: {data.vat_rate}%
          </div>
        </>
      )}

      {selectedCustomer && (
          <CustomerDetailsModal
            customerId={selectedCustomer.customer_id}
            displayName={selectedCustomer.customer_name}
            onClose={() => setSelectedCustomer(null)}
          />
      )}
    </div>
  );
}
